// Opstarten: stijlen, spelstand, spel en interface, plus service worker (offline en updates).
import './styles.css';
import './engine/polyfills.js';
import { registerSW } from 'virtual:pwa-register';
import { BezorgerGame } from './game/BezorgerGame.js';
import { Ui } from './ui/ui.js';
import { loadSave } from './state/save';
import { SHARE_PARAM } from './state/share';
import { onLanguageChange, t } from './i18n';
import { initAnalytics } from './utils/analytics';

const storage = (() => {
  try {
    const s = window.localStorage;
    s.getItem('test');
    return s;
  } catch {
    return null;
  }
})();

// Aanraakscherm? Dan tonen we de knoppen op het scherm.
const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || matchMedia('(pointer: coarse)').matches;
document.documentElement.classList.toggle('touch', isTouch);

// Een gedeelde score (?share=...): de waarde onthouden en de adresbalk direct opruimen, zonder de pagina te herladen.
const sharedRaw = (() => {
  try {
    const url = new URL(location.href);
    if (!url.searchParams.has(SHARE_PARAM)) return null;
    const raw = url.searchParams.get(SHARE_PARAM) ?? '';
    url.searchParams.delete(SHARE_PARAM);
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    return raw;
  } catch {
    return null;
  }
})();

// Google Analytics laadt alleen als de speler eerder toestemming gaf.
initAnalytics();

const ui = new Ui();
const save = loadSave(storage);
ui.applyLanguage(save.settings.language);
ui.preview(save);

// Eerst het startscherm tekenen, dan pas de stad opbouwen: zo staat er direct iets in beeld.
let booted = false;
const boot = () => {
  if (booted) return;
  booted = true;
  const game = new BezorgerGame({ canvas: document.getElementById('game-canvas'), save, storage, ui });
  ui.attach(game);
  if (sharedRaw !== null) ui.openShare(sharedRaw);
  game.setNightMode(game.save.settings.theme);
  // Handig bij testen en foutzoeken in de browserconsole.
  window.gameInstance = game;
};
requestAnimationFrame(() => setTimeout(boot, 0));
// Vangnet: in een tab op de achtergrond loopt requestAnimationFrame niet.
setTimeout(boot, 300);

// Oude caches van "Knus & Krokant — De Bezorger" (eigen sw.js) eenmalig opruimen.
if ('caches' in window) {
  caches
    .keys()
    .then((keys) => keys.filter((k) => k.startsWith('knus-bezorger')).forEach((k) => caches.delete(k)))
    .catch(() => {});
}

// Service worker: alles offline beschikbaar, en een melding als er een nieuwe versie is.
// Nooit automatisch herladen: dat zou een lopende shift afbreken.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  const updateSW = registerSW({
    onNeedRefresh() {
      ui.showUpdate(() => updateSW(true));
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => navigator.onLine && registration.update().catch(() => {});
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check());
      window.addEventListener('online', check);
      setInterval(check, 60 * 60 * 1000);
    },
  });
}

// Installeren als app
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  ui.setInstallPrompt(e);
});
const standalone = matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
if (!standalone && /iPad|iPhone|iPod/.test(navigator.userAgent)) document.getElementById('btn-install')?.classList.remove('hidden');

// Offline-melding
const banner = document.getElementById('offline-banner');
let backOnlineTimer;
const bannerText = () => t(navigator.onLine ? 'offline.back' : 'offline.banner');
const syncOnline = () => {
  clearTimeout(backOnlineTimer);
  if (navigator.onLine) {
    if (!banner.classList.contains('hidden')) {
      banner.textContent = bannerText();
      backOnlineTimer = setTimeout(() => banner.classList.add('hidden'), 2000);
    }
  } else {
    banner.textContent = bannerText();
    banner.classList.remove('hidden');
  }
};
onLanguageChange(() => {
  if (!banner.classList.contains('hidden')) banner.textContent = bannerText();
});
window.addEventListener('online', syncOnline);
window.addEventListener('offline', syncOnline);
syncOnline();
